import React, { useState, useCallback, useRef } from 'react';
import { MetricConfig } from '../types';
import { SelfAssessmentResponseService, SubmitResponseInput } from '../services/selfAssessmentResponseService';
import { AppDialog } from './AppDialog';
import { useDialogA11y } from '../hooks/useDialogA11y';
import './SelfAssessmentModal.css';

interface SelfAssessmentModalProps {
  isOpen: boolean;
  metrics: MetricConfig[];
  facilityId: string;
  residentId: string;
  respondedBy: string;
  respondedByRole: 'STAFF' | 'resident';
  primaryColor?: string;
  secondaryColor?: string;
  onClose: () => void;
  onComplete: () => void;
}

interface Answer {
  metricConfigId: string;
  value: number | null;
  skipped: boolean;
}

export const SelfAssessmentModal: React.FC<SelfAssessmentModalProps> = ({
  isOpen,
  metrics,
  facilityId,
  residentId,
  respondedBy,
  respondedByRole,
  primaryColor = '#667eea',
  secondaryColor = '#764ba2',
  onClose,
  onComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>(() =>
    metrics.map((m) => ({ metricConfigId: m.id, value: null, skipped: false }))
  );
  const [inputValue, setInputValue] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const totalQuestions = metrics.length;
  const answeredCount = answers.filter((a) => a.value !== null || a.skipped).length;
  const progressPercent = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const currentMetric = metrics[currentIndex];
  const currentAnswer = answers[currentIndex];

  const syncInputToAnswer = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed === '') return;
    const numVal = parseFloat(trimmed);
    if (isNaN(numVal)) return;
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = { ...next[currentIndex], value: numVal, skipped: false };
      return next;
    });
  }, [inputValue, currentIndex]);

  const goToQuestion = (index: number) => {
    syncInputToAnswer();
    setCurrentIndex(index);
    const existingValue = answers[index]?.value;
    setInputValue(existingValue !== null ? String(existingValue) : '');
    setShowReview(false);
  };

  const handleNext = () => {
    syncInputToAnswer();
    if (currentIndex < totalQuestions - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      const existingValue = answers[nextIndex]?.value;
      setInputValue(existingValue !== null ? String(existingValue) : '');
    } else {
      setShowReview(true);
    }
  };

  const handleBack = () => {
    if (showReview) {
      setShowReview(false);
      setCurrentIndex(totalQuestions - 1);
      const existingValue = answers[totalQuestions - 1]?.value;
      setInputValue(existingValue !== null ? String(existingValue) : '');
      return;
    }
    if (currentIndex > 0) {
      syncInputToAnswer();
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      const existingValue = answers[prevIndex]?.value;
      setInputValue(existingValue !== null ? String(existingValue) : '');
    }
  };

  const handleSkip = () => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = { ...next[currentIndex], value: null, skipped: true };
      return next;
    });
    if (currentIndex < totalQuestions - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      const existingValue = answers[nextIndex]?.value;
      setInputValue(existingValue !== null ? String(existingValue) : '');
    } else {
      setShowReview(true);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const responses: SubmitResponseInput[] = answers
        .filter((a) => a.value !== null && !a.skipped)
        .map((a) => ({ metricConfigId: a.metricConfigId, value: a.value! }));

      if (responses.length === 0) {
        setSubmitError('Please answer at least one question before submitting.');
        setSubmitting(false);
        return;
      }

      await SelfAssessmentResponseService.submitResponses(
        facilityId,
        residentId,
        respondedBy,
        respondedByRole,
        responses
      );
      onComplete();
    } catch (error) {
      console.error('Failed to submit self-assessment:', error);
      setSubmitError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAttempt = () => {
    if (answeredCount > 0) {
      setShowAbandonConfirm(true);
    } else {
      onClose();
    }
  };

  const saModalRef = useRef<HTMLDivElement>(null);
  // The nested abandon-confirm AppDialog has its own Escape handling. When it's open,
  // Escape must close only the confirm, not this outer modal too — so this hook's own
  // Escape is a no-op while the confirm is up.
  useDialogA11y({
    isOpen,
    onClose: () => {
      if (!showAbandonConfirm) handleCloseAttempt();
    },
    dialogRef: saModalRef,
  });

  const getRangeHint = (metric: MetricConfig): string | null => {
    const parts: string[] = [];
    const allMins = [metric.greenMin, metric.yellowMin, metric.redMin].filter(
      (v): v is number => v !== undefined && v !== null
    );
    const allMaxes = [metric.greenMax, metric.yellowMax, metric.redMax].filter(
      (v): v is number => v !== undefined && v !== null
    );
    if (allMins.length > 0 || allMaxes.length > 0) {
      const min = allMins.length > 0 ? Math.min(...allMins) : null;
      const max = allMaxes.length > 0 ? Math.max(...allMaxes) : null;
      if (min !== null && max !== null) {
        parts.push(`Range: ${min} – ${max}`);
      }
    }
    if (metric.targetValue !== undefined && metric.targetValue !== null) {
      parts.push(`Target: ${metric.targetValue}`);
    }
    return parts.length > 0 ? parts.join('  |  ') : null;
  };

  if (!isOpen || totalQuestions === 0) return null;

  return (
    <>
      <AppDialog
        isOpen={showAbandonConfirm}
        title="Abandon assessment?"
        description="You have unsaved answers. Are you sure you want to close?"
        onClose={() => setShowAbandonConfirm(false)}
        secondaryAction={{
          label: 'Continue Assessment',
          onClick: () => setShowAbandonConfirm(false),
          variant: 'primary'
        }}
        primaryAction={{
          label: 'Discard & Close',
          onClick: () => { setShowAbandonConfirm(false); onClose(); },
          variant: 'danger'
        }}
      />

      <div className="sa-modal-overlay" onClick={handleCloseAttempt}>
        <div
          ref={saModalRef}
          tabIndex={-1}
          className="sa-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sa-modal-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            '--brand-primary': primaryColor,
            '--brand-secondary': secondaryColor,
            '--brand-gradient': `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
          } as React.CSSProperties}
        >
          {/* Header */}
          <div className="sa-modal__header">
            <h2 className="sa-modal__title" id="sa-modal-title">Self-Assessment</h2>
            <button className="sa-modal__close" onClick={handleCloseAttempt} aria-label="Close">
              &times;
            </button>
          </div>

          {/* Progress bar */}
          <div className="sa-modal__progress">
            <div className="sa-modal__progress-bar">
              <div
                className="sa-modal__progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="sa-modal__progress-text">
              {showReview
                ? `Review – ${answeredCount} of ${totalQuestions} answered`
                : `Question ${currentIndex + 1} of ${totalQuestions} (${progressPercent}%)`}
            </div>
          </div>

          {/* Content area */}
          <div className="sa-modal__body">
            {showReview ? (
              /* Review screen */
              <div className="sa-review">
                <h3 className="sa-review__heading">Review Your Answers</h3>
                <div className="sa-review__list">
                  {metrics.map((metric, idx) => {
                    const ans = answers[idx];
                    return (
                      <div
                        key={metric.id}
                        className="sa-review__item"
                        onClick={() => goToQuestion(idx)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') goToQuestion(idx); }}
                      >
                        <div className="sa-review__item-label">
                          {metric.selfAssessmentPrompt || metric.displayName}
                        </div>
                        <div className={`sa-review__item-value ${ans.skipped ? 'skipped' : ans.value === null ? 'unanswered' : ''}`}>
                          {ans.skipped
                            ? 'Skipped'
                            : ans.value !== null
                              ? `${ans.value}${metric.units ? ` ${metric.units}` : ''}`
                              : 'Not answered'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {submitError && (
                  <div className="sa-modal__error">{submitError}</div>
                )}
              </div>
            ) : (
              /* Question screen */
              <div className="sa-question">
                <p className="sa-question__prompt">
                  {currentMetric.selfAssessmentPrompt || currentMetric.displayName}
                </p>

                {currentMetric.units && (
                  <span className="sa-question__units">{currentMetric.units}</span>
                )}

                <input
                  className="sa-question__input"
                  type="number"
                  step="any"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter value"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleNext(); }}
                />

                {getRangeHint(currentMetric) && (
                  <p className="sa-question__hint">{getRangeHint(currentMetric)}</p>
                )}

                {currentAnswer?.skipped && (
                  <p className="sa-question__skipped-label">Previously skipped</p>
                )}
              </div>
            )}
          </div>

          {/* Footer navigation */}
          <div className="sa-modal__footer">
            {showReview ? (
              <>
                <button className="btn btn--secondary sa-btn" onClick={handleBack}>
                  Back
                </button>
                <button
                  className="btn btn--primary sa-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Assessment'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn--secondary sa-btn"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                >
                  Back
                </button>
                <button className="sa-btn sa-btn--skip" onClick={handleSkip}>
                  Skip
                </button>
                <button className="btn btn--primary sa-btn" onClick={handleNext}>
                  {currentIndex === totalQuestions - 1 ? 'Review' : 'Next'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
